import api from '@/libs/axiosInstance';
import type { ApiResponse } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

interface AffiliatesCountFilters {
  status?: 'active' | 'inactive' | 'all';
}

interface AffiliatesCountResponse {
  count: number;
}

/**
 * Lightweight affiliates count for the sidebar badge. Dedicated endpoint so the
 * badge does not trigger the full affiliates listing/analytics pipeline the
 * way a perPage=1 search would.
 */
const fetchAffiliatesCount = async (
  filters: AffiliatesCountFilters
): Promise<AffiliatesCountResponse> => {
  const params = new URLSearchParams();
  if (filters.status) {
    params.append('status', filters.status);
  }

  const query = params.toString();
  const { data } = await api.get<ApiResponse<AffiliatesCountResponse>>(
    `/dashboard/affiliates/count${query ? `?${query}` : ''}`
  );

  return data.data ?? { count: 0 };
};

export const useAffiliatesCountQuery = (filters: AffiliatesCountFilters = {}) => {
  return useQuery<AffiliatesCountResponse, Error>({
    queryKey: ['affiliates', 'count', filters],
    queryFn: () => fetchAffiliatesCount(filters),
    // Aligned with the other sidebar counters (invitation stats).
    staleTime: 5 * 60 * 1000,
  });
};
